import { push } from 'connected-react-router';
import { all, call, fork, put, select, spawn, take } from 'redux-saga/effects';
import * as form from '../../containers/FormMessage/actions';
import * as notify from '../../containers/layout/Notifications/actions';
import { HOME } from '../../routes/paths';
import { endOfWeek } from '../../utils/dates/date';
import { serviceUnavailable } from '../app/actions';
import {
  ConnectionError,
  NotFoundError,
  ServiceUnavailableError,
  UnauthorizedAccessError
} from '../app/errors';
import { restartUserJourney } from '../auth/saga';
import { getBets } from '../bets/rest';
import { close, open } from '../modal/actions';
import * as user from '../user/selectors';
import { ADD_SLIP_NUMBER, disableAdd, VIEW_ENTER } from './actions';
import {
  SlipConflictError,
  SlipExpiredError,
  SlipIncorrectError,
  SlipUnsupportedShopError
} from './errors';
import { getAnonymousTrackConfig, postAssociateSlip } from './rest';
import { getSlipIds } from './selectors';
import { getSlips, setSlips } from './storage';

/* eslint-disable no-constant-condition */
function* checkAnonymousPreconditions(slipId = false) {
  let slips;

  try {
    slips = yield call(getSlips);
  } catch (e) {
    if (e instanceof SlipExpiredError) {
      yield call(restartUserJourney);
      yield put(form.setMessage(form.ERROR,
        'Your tracked slips are cleared at the end of each week. '
        + 'Re-enter any slips you\'d like to continue to track. '
        + 'Pick up a card in Shop for unlimited tracking'
      ));
    } else {
      throw e;
    }
    return false;
  }

  if (slipId && slips.includes(slipId)) {
    yield put(form.setMessage(
      form.ERROR,
      'You are already tracking this slip',
      { betSlipNumber: ' ' }
    ));
    return false;
  }
  let slipLimit;
  try {
    yield put(form.clearMessage());
    yield put(open({ componentName: 'RequestInProgress' }));
    const config = yield call(getAnonymousTrackConfig);
    yield put(close());
    slipLimit = config.maxAnonTrack;
  } catch (e) {
    yield put(close());
    slipLimit = api_config.betslip_limit;
  }
  if (slips.length >= slipLimit) {
    yield put(form.setMessage(
      form.ERROR,
      'You have reached the maximum number of trackable slips.'
      + ' Pick up a card in Shop for unlimited tracking'
    ));
    yield put(disableAdd());
    return false;
  }

  yield put(form.clearMessage());
  return slips;
}

export function* watchViewEnter() {
  while (yield take(VIEW_ENTER)) {
    const isAnonymous = yield select(user.isAnonymous);

    if (isAnonymous) {
      yield call(checkAnonymousPreconditions);
    }
  }
}

export function* addSlipForAnonymous(slipId) {
  const slips = yield call(checkAnonymousPreconditions, slipId);

  if (slips === false) {
    return;
  }

  try {
    yield put(open({ componentName: 'RequestInProgress' }));
    yield call(getBets, [slipId]);
    yield put(close());
    slips.push(slipId);
    yield call(setSlips, slips, endOfWeek(new Date()).getTime());
    yield put(push(HOME));
    yield put(notify.add('Bet tracked.', notify.INFO));
  } catch (e) {
    yield put(close());
    if (e instanceof ConnectionError) {
      yield put(notify.add('Connection error.', notify.ERROR));
    } else if (e instanceof ServiceUnavailableError) {
      yield put(serviceUnavailable());
    } else if (e instanceof NotFoundError) {
      yield put(form.setMessage(
        form.ERROR,
        'There was a problem adding your bet. Please check the highlighted field.',
        { betSlipNumber: 'You have entered an invalid slip number' }
      ));
    } else if (e instanceof UnauthorizedAccessError) {
      yield put(form.setMessage(
        form.ERROR,
        'There was a problem adding your bet. Please check the highlighted field.',
        { betSlipNumber: 'You cannot track this slip!' }
      ));
    } else if (e instanceof SlipConflictError) {
      yield put(form.setMessage(
        form.ERROR,
        'You cannot track this slip as it is already being tracked by another account',
        { betSlipNumber: ' ' }
      ));
    } else if (e instanceof SlipUnsupportedShopError) {
      yield put(form.setMessage(
        form.ERROR,
        'Sorry, Bet Tracker is currently in limited trial.'
        + ' Check with your local shop for availability.',
        { betSlipNumber: ' ' }
      ));
    } else {
      throw e;
    }
  }
}

export function* associateSlipRequest(slipId) {
  const slips = yield select(getSlipIds);

  if (slips.includes(slipId)) {
    yield put(form.setMessage(
      form.ERROR,
      'You are already tracking this slip',
      { betSlipNumber: ' ' }
    ));
  } else {
    try {
      yield put(form.clearMessage());
      yield put(open({ componentName: 'RequestInProgress' }));
      yield call(postAssociateSlip, slipId);
      yield put(close());
      yield put(push(HOME));
      yield put(notify.add('Bet tracked.', notify.INFO));
    } catch (e) {
      yield put(close());
      if (e instanceof ConnectionError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else if (e instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else if (e instanceof UnauthorizedAccessError) {
        yield call(restartUserJourney);
      } else if (e instanceof NotFoundError || e instanceof SlipIncorrectError) {
        yield put(form.setMessage(
          form.ERROR,
          'There was a problem adding your bet. Please check the highlighted field.',
          { betSlipNumber: 'You have entered an invalid slip number' }
        ));
      } else if (e instanceof SlipConflictError) {
        yield put(form.setMessage(
          form.ERROR,
          'You cannot track this slip as it is already being tracked by another account',
          { betSlipNumber: ' ' }
        ));
      } else if (e instanceof SlipUnsupportedShopError) {
        yield put(form.setMessage(
          form.ERROR,
          'Sorry, Bet Tracker is currently in limited trial.'
          + ' Check with your local shop for availability.',
          { betSlipNumber: ' ' }
        ));
      } else {
        throw e;
      }
    }
  }
}

export function* watchAddSlipNumber() {
  while (true) {
    const action = yield take(ADD_SLIP_NUMBER);
    const isAnonymous = yield select(user.isAnonymous);
    const isAuthenticated = yield select(user.isAuthenticated);
    const { slipId } = action;

    if (isAnonymous) {
      yield fork(addSlipForAnonymous, slipId);
    } else if (isAuthenticated) {
      yield fork(associateSlipRequest, slipId);
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchViewEnter),
    spawn(watchAddSlipNumber)
  ]);
}
/* eslint-enable no-constant-condition */
